"use client"

import CustomButton from "@/(admin)/components/button/button"
import { t } from "@/languages/i18n"
import useIsMobile from "@/lib/config/lib/use-is-mobile"
import { DefaultImage } from "@/lib/external-components"
import { getImageUrl } from "@/lib/get-image-url"
import { generateImagePath } from "@/lib/utils"
import { ComponentConfig } from "@measured/puck"
import Head from "next/head"
import Image from "next/image"
import "swiper/css"
import "swiper/css/effect-fade"
import "swiper/css/navigation"
import "swiper/css/pagination"
import { Autoplay, EffectFade, Navigation, Pagination } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import { CommonStylesProps } from "../../../lib/commonCSSProps"
import { generateResponsiveCSS } from "../../../lib/helper"
import "./customCarousel.css"
export interface CarouselProp extends CommonStylesProps {
  items?: {
    imageUrl: string | { url: string; alternativeText?: string; caption?: string }
    title?: string
    description?: string
    buttonText?: string
    buttonUrl?: string
  }[]
  titleStyle?: {
    desktop?: {
      fontSize?: string
      fontWeight?: string
      lineHeight?: string
      color?: string
    }
    mobile?: {
      fontSize?: string
      fontWeight?: string
      lineHeight?: string
      color?: string
    }
  }
  descriptionStyle?: {
    desktop?: {
      fontSize?: string
      fontWeight?: string
      lineHeight?: string
      color?: string
    }
    mobile?: {
      fontSize?: string
      fontWeight?: string
      lineHeight?: string
      color?: string
    }
  }
}

export const RenderConfig: ComponentConfig<CarouselProp> = {
  render: ({ items, styles, responsiveType, titleStyle, descriptionStyle }: CarouselProp) => {
    const id = `caocao-carousel-${Math.random().toString(36).substr(2, 9)}`
    const responsiveCSS = generateResponsiveCSS(id, styles || {}, responsiveType)
    const isMobile = useIsMobile()
    // Extract first image URL from object if needed
    const firstImageUrl = items?.[0]?.imageUrl
      ? typeof items[0].imageUrl === "object"
        ? items[0].imageUrl.url
        : items[0].imageUrl
      : null
    const firstImage = firstImageUrl ? generateImagePath(firstImageUrl, "large") : DefaultImage

    return (
      <>
        <Head>
          <link rel="preload" as="image" href={firstImage} fetchPriority="high" />
        </Head>
        {responsiveCSS}
        {items && items.length > 0 ? (
          <div id={id} className="relative h-screen">
            <Swiper
              modules={[Pagination, Autoplay, EffectFade, Navigation]}
              spaceBetween={30}
              slidesPerView={1}
              effect={"fade"}
              className="h-full w-full"
              speed={800}
              pagination={{
                clickable: true,
                dynamicBullets: false,
                bulletClass: "custom-swiper-pagination-bullet",
                bulletActiveClass: "custom-swiper-pagination-bullet-active",
                el: ".swiper-pagination",
                renderBullet: function (index, className) {
                  return '<span class="' + className + '"></span>'
                },
              }}
              navigation={{
                nextEl: ".swiper-button-next",
                prevEl: ".swiper-button-prev",
              }}
              // autoplay={{
              //   delay: 5000,
              //   disableOnInteraction: false,
              //   pauseOnMouseEnter: true,
              // }}
              loop={true}
              watchSlidesProgress={true}
            >
              {items.map((item, index) => {
                // Extract url and alt text from imageUrl object
                const imageUrl = getImageUrl(item.imageUrl)
                const generatedImageUrl = imageUrl ? generateImagePath(imageUrl, "large") : DefaultImage

                const imageAlt =
                  (typeof item.imageUrl === "object" && item.imageUrl?.alternativeText) ||
                  item.title ||
                  `Slide ${index + 1}`

                return (
                  <SwiperSlide key={index}>
                    <div className="relative w-full h-screen">
                      <Image
                        src={generatedImageUrl}
                        alt={imageAlt}
                        fill
                        className="object-cover"
                        sizes="80vw"
                        priority
                        fetchPriority={index === 0 ? "high" : "auto"}
                        placeholder="blur"
                        blurDataURL="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2YwZjBmMCIvPjwvc3ZnPg=="
                      />
                      <div className="absolute inset-0 bg-[#0000004D] z-10 pointer-events-none" />
                      <div className="absolute max-w-[1440px] mx-auto inset-0 ">
                        <div
                          className={`absolute z-20 right-auto lg:left-[120px] left-6 w-2/3 flex flex-col gap-6 ${
                            isMobile ? "items-start justify-center top-[122px] bottom-0" : ""
                          }`}
                          style={
                            isMobile
                              ? {
                                  top: "0",
                                  bottom: "0",
                                  transform: "translateX(0)",
                                }
                              : {
                                  top: "215px",
                                }
                          }
                        >
                          <div className={`flex flex-col gap-4 ${isMobile ? "items-start" : ""}`}>
                            {item.title && (
                              <h1
                                className="lg:text-[74px] text-left text-white font-extrabold lg:font-bold p-0 m-0 right-auto text-[40px]"
                                style={{
                                  fontSize:
                                    (isMobile ? titleStyle?.mobile?.fontSize : titleStyle?.desktop?.fontSize) ||
                                    (isMobile ? "40px" : "74px"),
                                  fontWeight:
                                    (isMobile ? titleStyle?.mobile?.fontWeight : titleStyle?.desktop?.fontWeight) ||
                                    (isMobile ? "extrabold" : "bold"),
                                  lineHeight:
                                    (isMobile ? titleStyle?.mobile?.lineHeight : titleStyle?.desktop?.lineHeight) ||
                                    (isMobile ? "48px" : "80px"),
                                  color: (isMobile ? titleStyle?.mobile?.color : titleStyle?.desktop?.color) || "white",
                                  textShadow: "0px 2px 3px rgba(0, 0, 0, 0.3), 0px 6px 10px rgba(0, 0, 0, 0.1)",
                                }}
                              >
                                {item.title}
                              </h1>
                            )}
                            {item.description && (
                              <p
                                className="text-base text-left leading-6 text-white font-semibold p-0 m-0 right-auto"
                                style={{
                                  fontSize:
                                    (isMobile
                                      ? descriptionStyle?.mobile?.fontSize
                                      : descriptionStyle?.desktop?.fontSize) || "16px",
                                  fontWeight:
                                    (isMobile
                                      ? descriptionStyle?.mobile?.fontWeight
                                      : descriptionStyle?.desktop?.fontWeight) || "semibold",
                                  lineHeight:
                                    (isMobile
                                      ? descriptionStyle?.mobile?.lineHeight
                                      : descriptionStyle?.desktop?.lineHeight) || "24px",
                                  color:
                                    (isMobile ? descriptionStyle?.mobile?.color : descriptionStyle?.desktop?.color) ||
                                    "white",
                                  textShadow: "0px 2px 3px rgba(0, 0, 0, 0.3), 0px 6px 10px rgba(0, 0, 0, 0.1)",
                                }}
                              >
                                {item.description}
                              </p>
                            )}
                          </div>
                          <div className={`${isMobile ? "flex justify-start w-full" : "w-fit"}`}>
                            {item.buttonText && (
                              <CustomButton
                                text={item.buttonText}
                                onClick={() => {
                                  const raw = (item.buttonUrl || "").trim()
                                  if (!raw) return

                                  if (raw.startsWith("#")) {
                                    const el = document.querySelector(raw)
                                    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" })
                                    else window.location.hash = raw
                                    return
                                  }

                                  if (raw.startsWith("/")) {
                                    const [path, hash] = raw.split("#")
                                    if (hash && window.location.pathname === path) {
                                      const el = document.getElementById(hash)
                                      if (el) {
                                        el.scrollIntoView({ behavior: "smooth", block: "start" })
                                        return
                                      }
                                    }
                                  }

                                  window.location.href = raw
                                }}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </SwiperSlide>
                )
              })}
            </Swiper>
            <div className="swiper-pagination"></div>

            {/* Custom Navigation Buttons */}
            <button className="swiper-button-prev custom-swiper-nav-btn" aria-label="Previous slide"></button>
            <button className="swiper-button-next custom-swiper-nav-btn" aria-label="Next slide"></button>
          </div>
        ) : (
          <div>{t("No items")}</div>
        )}
      </>
    )
  },
}
